import React, { useState, useMemo, useEffect } from "react";
import { Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { FaEye } from "react-icons/fa";
ChartJS.register(ArcElement, Tooltip, Legend);

const ANCDataVisualizer = ({
    data = {
        totalNewANC: 1214663,
        firstTrimester: 1158945,
        fourOrMoreANC: 1256974,
    },
    selectedDistrict = null,
    seed = 0, // district click seed
}) => {
    const [open, setOpen] = useState(false);

    // ------------------- Generate district-level variation -------------------
    const effectiveData = useMemo(() => {
        if (!selectedDistrict) return data;

        // Simple seeded pseudo-random for demo
        const seededRandom = (str, s) => {
            let h = 2166136261 >>> 0;
            for (let i = 0; i < str.length; i++) {
                h = Math.imul(h ^ str.charCodeAt(i), 16777619);
            }
            h += s;
            return Math.abs((h >>> 0) % 1000000); // arbitrary range
        };

        const totalNewANC = data.totalNewANC - seededRandom(selectedDistrict, seed) + 1000;
        const firstTrimester = Math.min(totalNewANC, data.firstTrimester - seededRandom(selectedDistrict, seed) % 50000);
        const fourOrMoreANC = Math.min(totalNewANC, data.fourOrMoreANC - seededRandom(selectedDistrict, seed) % 50000);

        return {
            totalNewANC,
            firstTrimester,
            fourOrMoreANC,
        };
    }, [data, selectedDistrict, seed]);

    // ------------------- Compute percentages -------------------
    const trimesterPerc = ((effectiveData.firstTrimester / effectiveData.totalNewANC) * 100).toFixed(2);
    const fourOrMorePerc = ((effectiveData.fourOrMoreANC / effectiveData.totalNewANC) * 100).toFixed(2);

    const trimesterChartData = {
        labels: ["1st Trimester", "Later"],
        datasets: [
            {
                data: [effectiveData.firstTrimester, effectiveData.totalNewANC - effectiveData.firstTrimester],
                backgroundColor: ["#5e72e4", "#dbeafe"],
                borderWidth: 1,
            },
        ],
    };

    const fourOrMoreChartData = {
        labels: ["4+ ANC Checkups", "<4 ANC"],
        datasets: [
            {
                data: [effectiveData.fourOrMoreANC, effectiveData.totalNewANC - effectiveData.fourOrMoreANC],
                backgroundColor: ["#06b6d4", "#e0f2f1"],
                borderWidth: 1,
            },
        ],
    };

    // ------------------- Close offcanvas if district changes -------------------
    useEffect(() => {
        setOpen(false);
    }, [selectedDistrict]);

    return (
        <>
            {/* Dashboard card */}
            <button
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-3 px-3 py-2 rounded-lg shadow-sm bg-white border hover:shadow-md w-full"
            >
                <div
                    className="w-10 h-10 flex items-center justify-center rounded-md"
                    style={{ background: "#eef2ff" }}
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 2C13.1046 2 14 2.89543 14 4C14 5.10457 13.1046 6 12 6C10.8954 6 10 5.10457 10 4C10 2.89543 10.8954 2 12 2ZM6 22C6 18.6863 8.68629 16 12 16C15.3137 16 18 18.6863 18 22H6Z"
                            fill="#5e72e4"
                        />
                    </svg>
                </div>

                <div className="text-left">
                    <div className="text-xs text-slate-500">Total NEW ANC</div>
                    <div className="text-lg font-semibold">{effectiveData.totalNewANC.toLocaleString('en-IN')}</div>
                </div>
            </button>

            {/* Offcanvas */}
            {open && (
                <div className="fixed inset-0 z-50 flex">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setOpen(false)}
                    />

                    <aside className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-auto p-4">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">ANC Data</h3>
                                <p className="text-xs text-slate-500">Source: HMIS Apr24 - Mar25</p>
                                {selectedDistrict && (
                                    <p className="text-xs text-indigo-600 mt-1">District: {selectedDistrict}</p>
                                )}
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="px-3 py-1 rounded bg-slate-100 text-sm"
                            >
                                Close
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* 1st Trimester */}
                            <div className="bg-white p-4 rounded-lg border flex flex-col items-center">
                                <h5 className="text-sm font-medium mb-2 text-center">Registered within 1st Trimester</h5>
                                <Doughnut data={trimesterChartData} />
                                <div className="mt-2 text-sm font-semibold">{effectiveData.firstTrimester.toLocaleString('en-IN')} ({trimesterPerc}%)</div>
                            </div>

                            {/* 4+ ANC Checkups */}
                            <div className="bg-white p-4 rounded-lg border flex flex-col items-center">
                                <h5 className="text-sm font-medium mb-2 text-center">Received 4+ ANC Checkups</h5>
                                <Doughnut data={fourOrMoreChartData} />
                                <div className="mt-2 text-sm font-semibold">{effectiveData.fourOrMoreANC.toLocaleString('en-IN')} ({fourOrMorePerc}%)</div>
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </>
    );
};

export default ANCDataVisualizer;
